import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import type { ChannelConfigField } from '../_components/ChannelConfig/types';
import { getClawChannelConfig, setClawChannelConfig } from '../apis';

type ChannelConfigStatus = 'idle' | 'loading' | 'ready' | 'saving' | 'error';
type ChannelConfigFormValue = string | boolean;

interface UseChannelConfigOptions {
  clawId: string;
  channelId: string;
  fields: ChannelConfigField[];
  fixedConfig?: Record<string, unknown>;
  onSuccess?: () => void;
}

const splitArrayValue = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const parseObjectValue = (value: string): Record<string, unknown> => {
  const parsedValue = JSON.parse(value);

  if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
    throw new Error('Invalid object value');
  }

  return parsedValue as Record<string, unknown>;
};

const normalizeFieldValue = (field: ChannelConfigField, value: unknown): ChannelConfigFormValue => {
  if (field.valueType === 'boolean') {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value === 'true';
    }

    return typeof field.defaultValue === 'boolean' ? field.defaultValue : false;
  }

  if (field.valueType === 'array') {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join('\n');
    }

    if (typeof value === 'string') {
      return value;
    }

    return typeof field.defaultValue === 'string' ? field.defaultValue : '';
  }

  if (field.valueType === 'object') {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object' && value !== null) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (_error) {
        return typeof field.defaultValue === 'string' ? field.defaultValue : '';
      }
    }

    return typeof field.defaultValue === 'string' ? field.defaultValue : '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === undefined || value === null) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch (_error) {
    return typeof field.defaultValue === 'string' ? field.defaultValue : '';
  }
};

const serializeFieldValue = (
  field: ChannelConfigField,
  value: ChannelConfigFormValue,
): string | boolean | Record<string, unknown> => {
  if (field.valueType === 'boolean') {
    return typeof value === 'boolean' ? value : value === 'true';
  }

  if (field.valueType === 'array') {
    return splitArrayValue(typeof value === 'string' ? value : '').join(',');
  }

  if (field.valueType === 'object') {
    const normalizedValue = typeof value === 'string' ? value.trim() : String(value).trim();

    if (!normalizedValue) {
      return {};
    }

    return parseObjectValue(normalizedValue);
  }

  return typeof value === 'string' ? value.trim() : String(value);
};

export const useChannelConfig = ({ clawId, channelId, fields, fixedConfig, onSuccess }: UseChannelConfigOptions) => {
  const t = useTranslations('claw.channelConfig');
  const [status, setStatus] = useState<ChannelConfigStatus>('idle');
  const [values, setValues] = useState<Record<string, ChannelConfigFormValue>>({});
  const [errorMessage, setErrorMessage] = useState('');
  const requestIdRef = useRef(0);

  const loadConfig = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus('loading');
    setErrorMessage('');

    try {
      const { data, error } = await getClawChannelConfig(clawId, channelId);

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (error) {
        setStatus('error');
        setErrorMessage(error.message || t('errors.loadConfig'));
        return;
      }

      const config = data?.config ?? {};
      const nextValues: Record<string, ChannelConfigFormValue> = {};

      fields.forEach((field) => {
        const value = config[field.key];
        nextValues[field.key] = normalizeFieldValue(field, value);
      });

      setValues(nextValues);
      setStatus('ready');
    } catch (_error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setStatus('error');
      setErrorMessage(t('errors.network'));
    }
  }, [channelId, clawId, fields, t]);

  const updateValue = useCallback((key: string, value: ChannelConfigFormValue) => {
    setValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
  }, []);

  const submitConfig = useCallback(async (): Promise<{ error?: string }> => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    for (const field of fields) {
      const rawValue = values[field.key];
      const nextValue = field.valueType === 'boolean' ? rawValue : typeof rawValue === 'string' ? rawValue.trim() : '';

      if (field.valueType === 'array') {
        if (!field.required) {
          continue;
        }

        if (!splitArrayValue(typeof nextValue === 'string' ? nextValue : '').length) {
          return { error: t('errors.requiredField', { field: t(field.labelKey) }) };
        }
        continue;
      }

      if (field.valueType === 'object') {
        if (!nextValue) {
          if (!field.required) {
            continue;
          }

          return { error: t('errors.requiredField', { field: t(field.labelKey) }) };
        }

        try {
          parseObjectValue(typeof nextValue === 'string' ? nextValue : String(nextValue));
        } catch (_error) {
          return { error: t('errors.invalidJsonField', { field: t(field.labelKey) }) };
        }

        continue;
      }

      if (!field.required) {
        continue;
      }

      if (!nextValue) {
        return { error: t('errors.requiredField', { field: t(field.labelKey) }) };
      }
    }

    setStatus('saving');
    setErrorMessage('');

    const nextConfig: Record<string, unknown> = {
      ...(fixedConfig ?? {}),
    };

    fields.forEach((field) => {
      const nextValue = values[field.key] ?? field.defaultValue ?? '';
      nextConfig[field.key] = serializeFieldValue(field, nextValue);
    });

    try {
      const { error } = await setClawChannelConfig(channelId, {
        claw_id: clawId,
        config: nextConfig,
      });

      if (requestId !== requestIdRef.current) {
        return {};
      }

      if (error) {
        setStatus('ready');
        return { error: error.message || t('errors.saveConfig') };
      }

      setStatus('ready');
      onSuccess?.();
      return {};
    } catch (_error) {
      if (requestId !== requestIdRef.current) {
        return {};
      }

      setStatus('ready');
      return { error: t('errors.network') };
    }
  }, [channelId, clawId, fields, fixedConfig, onSuccess, t, values]);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setStatus('idle');
    setValues({});
    setErrorMessage('');
  }, []);

  return {
    status,
    values,
    errorMessage,
    loadConfig,
    updateValue,
    submitConfig,
    reset,
  };
};
